import React from 'react';
import {
    Box,
    Card,
    CardContent,
    Typography,
    Grid,
    Chip,
    CircularProgress
} from '@mui/material';
import {
    TrendingUp as TrendingUpIcon,
    AccessTime as AccessTimeIcon,
    Savings as SavingsIcon,
    CalendarMonth as CalendarMonthIcon,
    CheckCircle as CheckCircleIcon,
    Warning as WarningIcon,
    Cancel as CancelIcon
} from '@mui/icons-material';
import { formatCurrency, formatNumber } from '@vortx/shared-ui';

/**
 * ResultsSummary - Executive Summary for Performance Tab
 *
 * Receives `analysis` prop from parent (ScenarioEditor) - same analysis used in FinancialAnalysis.
 * This ensures both Performance and Financials tabs show identical values.
 */
export default function ResultsSummary({ results, analysis, capexSource = 'custom', vppEstimate = null }) {

    if (!results || !results.outputs) return null;

    // Show loading state if no analysis
    if (!analysis) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <CircularProgress size={24} />
            </Box>
        );
    }

    // Access nested structure from FinancialCalculator.getComprehensiveAnalysis()
    const { summary, projectCashflows } = analysis;
    const { npv, irr, paybackPeriod, discountedPaybackPeriod, totalSavings } = summary || {};
    const analysisYears = analysis.projectCashflows?.length || 20;

    // Calculate Year 1 NET savings from cashflows (after O&M, fuel, and DER charges)
    const yearOneNetSavings = projectCashflows?.[0]?.netCashflow || 0;

    // Determine Viability Status
    let status = "neutral";
    let statusColor = "default";
    let statusLabel = "Unknown";
    let StatusIcon = AccessTimeIcon;

    if (npv > 0) {
        status = "positive";
        statusColor = "success";
        statusLabel = "Projects Savings";
        StatusIcon = CheckCircleIcon;
        if (paybackPeriod > 0 && paybackPeriod < 5) {
            statusLabel = "Excellent Investment";
        }
    } else if (npv < 0) {
        status = "negative";
        statusColor = "error";
        statusLabel = "Not Recommended";
        StatusIcon = CancelIcon;
    } else {
        status = "marginal";
        statusColor = "warning";
        statusLabel = "Marginal";
        StatusIcon = WarningIcon;
    }

    return (
        <Box sx={{ mb: 4 }}>
            {/* Header with Viability Badge */}
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, gap: 2, flexWrap: 'wrap' }}>
                <Chip
                    icon={<StatusIcon fontSize="small" />}
                    label={statusLabel}
                    color={statusColor}
                    size="small"
                />
                {capexSource === 'custom' && (
                    <Chip label="Custom CapEx" size="small" variant="outlined" />
                )}
            </Box>

            {/* Key Metrics Cards */}
            <Grid container spacing={2}>
                {/* NPV Card */}
                <Grid item xs={6} sm={3}>
                    <Card sx={{
                        bgcolor: status === 'positive' ? 'rgba(76, 175, 80, 0.08)' : 'background.paper',
                        height: '100%'
                    }}>
                        <CardContent>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                <TrendingUpIcon color={status === 'positive' ? 'success' : 'inherit'} fontSize="small" />
                                <Typography variant="body2" color="text.secondary">
                                    NPV
                                </Typography>
                            </Box>
                            <Typography variant="h4" sx={{
                                fontWeight: 'bold',
                                color: npv >= 0 ? 'success.main' : 'error.main'
                            }}>
                                {formatCurrency(npv)}
                            </Typography>
                            <Typography variant="caption" color="text.primary" sx={{ fontSize: '14px', fontWeight: 500, opacity: 0.9 }}>
                                {analysisYears || 20}-year lifecycle
                            </Typography>
                        </CardContent>
                    </Card>
                </Grid>

                {/* IRR Card */}
                <Grid item xs={6} sm={3}>
                    <Card sx={{ height: '100%' }}>
                        <CardContent>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                <TrendingUpIcon fontSize="small" />
                                <Typography variant="body2" color="text.secondary">
                                    IRR
                                </Typography>
                            </Box>
                            <Typography variant="h4" sx={{
                                fontWeight: 'bold',
                                color: irr != null && irr > 0 ? 'success.main' : 'text.primary'
                            }}>
                                {irr != null ? `${(irr * 100).toFixed(1)}%` : '—'}
                            </Typography>
                            <Typography variant="caption" color="text.primary" sx={{ fontSize: '14px', fontWeight: 500, opacity: 0.9 }}>
                                Internal rate of return
                            </Typography>
                        </CardContent>
                    </Card>
                </Grid>

                {/* Payback Period Card */}
                <Grid item xs={6} sm={3}>
                    <Card sx={{ height: '100%' }}>
                        <CardContent>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                <AccessTimeIcon fontSize="small" />
                                <Typography variant="body2" color="text.secondary">
                                    Payback
                                </Typography>
                            </Box>
                            <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
                                {paybackPeriod > 0 ? formatNumber(paybackPeriod, 1) : '—'}
                                <Typography component="span" variant="h6" sx={{ ml: 0.5 }}>
                                    yrs
                                </Typography>
                            </Typography>
                            <Typography variant="caption" color="text.primary" sx={{ fontSize: '14px', fontWeight: 500, opacity: 0.9 }}>
                                {discountedPaybackPeriod != null
                                    ? `${formatNumber(discountedPaybackPeriod, 1)} yrs discounted`
                                    : 'Simple payback'}
                            </Typography>
                        </CardContent>
                    </Card>
                </Grid>

                {/* Year 1 Net Savings Card */}
                <Grid item xs={6} sm={3}>
                    <Card sx={{ height: '100%' }}>
                        <CardContent>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                <CalendarMonthIcon fontSize="small" />
                                <Typography variant="body2" color="text.secondary">
                                    Year 1 Net Savings
                                </Typography>
                            </Box>
                            <Typography variant="h4" sx={{
                                fontWeight: 'bold',
                                color: yearOneNetSavings >= 0 ? 'success.main' : 'error.main'
                            }}>
                                {formatCurrency(yearOneNetSavings)}
                            </Typography>
                            <Typography variant="caption" color="text.primary" sx={{ fontSize: '14px', fontWeight: 500, opacity: 0.9 }}>
                                After O&M, fuel, and charges
                            </Typography>
                        </CardContent>
                    </Card>
                </Grid>
            </Grid>

            {/* VPP Revenue Indicator */}
            {vppEstimate && (vppEstimate.adjTotalAnnualLow > 0 || vppEstimate.adjTotalAnnualHigh > 0) && (
                <Box sx={{ mt: 2, display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
                    <Chip
                        label={`VPP (net): ${formatCurrency(vppEstimate.adjTotalAnnualLow)} \u2013 ${formatCurrency(vppEstimate.adjTotalAnnualHigh)}/yr`}
                        size="small"
                        variant="outlined"
                        sx={{ borderColor: 'warning.main', color: 'warning.main' }}
                    />
                    <Typography variant="caption" color="text.secondary">
                        Net wholesale market revenue after dispatch opportunity cost \u2014 toggle in Deal Analysis tab
                    </Typography>
                </Box>
            )}
        </Box>
    );
}
